import Skeleton from '@common/components/Skeleton';
import { Tooltip } from '@common/index';
import { useTranslation } from '@client-app/i18n';
import { Question } from '@phosphor-icons/react';
import clsx from 'clsx';
import React from 'react';
import IncludedIntegrations from './IncludedIntegrations';
import RollingPrice from './RollingPrice';

export interface IncludedAiModel {
    name: string;
    iconSrc: string;
    capabilityLabel?: string;
}

export interface PlanFeature {
    id: string;
    label: string;
    badge?: 'team-exclusive';
}

export interface PlanFeatureGroups {
    workspace: PlanFeature[];
    workUnits: PlanFeature[];
    trainingAndIntegrations: PlanFeature[];
}

interface PlanDataBase {
    name: string;
    price: string | number;
    pricePrefix?: string;
    priceSuffix?: string;
    pricePresentation?: 'default' | 'leading-period';
    comparePrice?: string | number;
    description: string;
    buttonText: string;
    bubbleText?: string | null;
    badgeText?: string;
    badgeAlign?: 'center' | 'right';
    cardTone?: 'default' | 'current-trial' | 'scheduled-trial';
    priceNote?: string;
    isPopular?: boolean;
    isCustom?: boolean;
    footerText?: string;
    footerActionLabel?: string;
    headerAction?: React.ReactNode;
    tierSelector?: React.ReactNode;
    trialBenefit?: {
        value: string;
    };
    workspaceGroupLabel?: string;
    builtInCapabilitiesLabel?: string;
    builtInModels?: IncludedAiModel[];
    estimatedCapacity?: string;
    highlightLimits?: boolean;
    showIntegrations?: boolean;
    showWorkUnitBenefitsInOnboarding?: boolean;
}

export type PlanData = PlanDataBase & (
    | {
        featureMode: 'grouped';
        featureGroups: PlanFeatureGroups;
    }
    | {
        featureMode: 'flat';
        features: string[];
    }
);

interface PricingCardProps {
    plan: PlanData;
    onAction: () => void;
    onFooterAction?: () => void;
    disabled?: boolean;
    loadingPlanName?: string;
    fetching?: boolean;
    isOnboarding?: boolean;
}

interface EstimatedCapacityFeatureProps {
    label: string;
    tooltip: string;
}

const renderFormattedText = (text: string) => {
    if (!text) return null;

    // Regex to capture:
    // 1. Number ranges, including one-digit and comma-formatted values (e.g. 8–12, 190–300, 1,875–3,000)
    // 2. AI Operator Work Units in English or Chinese, including the shared variant.
    // 3. Dollar amounts: \$\d{1,3}(?:,\d{3})*
    const regex = /(\d{1,3}(?:,\d{3})*[\–-]\d{1,3}(?:,\d{3})*|\d{1,3}(?:,\d{3})*(?:\s*个\s*(?:共享\s*)?AI\s*运营员工劳动值|\s+(?:shared\s+)?AI\s+Operator\s+Work\s+Units)|\$\d{1,3}(?:,\d{3})*)/g;

    const parts = text.split(regex);
    return (
        <>
            {parts.map((part, index) => {
                if (index % 2 === 1) {
                    return (
                        <span key={index} className="font-semibold text-text-main">
                            {part}
                        </span>
                    );
                }
                return part;
            })}
        </>
    );
};

const EstimatedCapacityFeature = ({ label, tooltip }: EstimatedCapacityFeatureProps) => (
    <li className="flex items-start gap-2.5 py-0.5">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
            ✓
        </span>
        <div className="text-sm font-normal leading-relaxed text-[#302E2A] min-w-0">
            <span>{renderFormattedText(label)}</span>
            <span className="inline-flex items-center align-middle ml-1">
                <Tooltip
                    content={tooltip}
                    side="top"
                    align="center"
                    sideOffset={6}
                    contentClassName="!max-w-[320px] !whitespace-normal !px-3 !py-2 !text-left !leading-5"
                >
                    <button
                        type="button"
                        aria-label={tooltip}
                        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[#8A93B2] transition-colors hover:text-[#1A162F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7BFF]/20"
                    >
                        <Question size={14} weight="bold" />
                    </button>
                </Tooltip>
            </span>
        </div>
    </li>
);

const PricingCard: React.FC<PricingCardProps> = ({
    plan, onAction, onFooterAction, disabled, loadingPlanName, fetching, isOnboarding = false
}) => {
    const { t } = useTranslation();
    const isLoading = loadingPlanName === plan.name;
    const isFetching = fetching;
    const isPopular = plan.isPopular;
    const isDisabledState = Boolean(disabled || isLoading || isFetching);
    const numericPrice = typeof plan.price === 'number' ? plan.price : Number.NaN;
    const hasNumericPrice = Number.isFinite(numericPrice);
    const numericComparePrice = typeof plan.comparePrice === 'number' ? plan.comparePrice : Number.NaN;
    const hasNumericComparePrice = Number.isFinite(numericComparePrice);
    const usesLeadingPeriodPrice = plan.pricePresentation === 'leading-period';
    const isCurrentTrialTone = plan.cardTone === 'current-trial';
    const isScheduledTrialTone = plan.cardTone === 'scheduled-trial';
    const hasFeatureGroups = plan.featureMode === 'grouped';
    const shouldShowPaidBenefits = !isOnboarding;
    const shouldShowWorkUnitBenefits = shouldShowPaidBenefits
        || Boolean(plan.showWorkUnitBenefitsInOnboarding);
    const shouldShowPlanSelectionLimits = hasFeatureGroups
        && plan.featureGroups.workspace.length > 0;
    const shouldShowBuiltInCapabilities = Boolean(
        plan.builtInCapabilitiesLabel && plan.builtInModels?.length
    );
    const shouldShowFeatureContent = shouldShowPaidBenefits
        || shouldShowPlanSelectionLimits
        || shouldShowBuiltInCapabilities;

    // 骨架屏渲染
    if (isFetching) {
        const groupedFeaturesCount = plan.featureMode === 'grouped'
            ? plan.featureGroups.workspace.length
                + plan.featureGroups.workUnits.length
                + plan.featureGroups.trainingAndIntegrations.length
            : 0;
        const featuresCount = groupedFeaturesCount
            || (plan.featureMode === 'flat' ? plan.features.length : 0)
            || 6;
        return (
            <div
                className={clsx(
                    'relative rounded-[24px] p-5 flex flex-col border bg-white border-gray-200 w-full'
                )}
            >
                {/* 标题骨架 */}
                <div className="mb-4">
                    <div className="flex items-baseline justify-between mb-1">
                        <Skeleton width="50%" height="20px" className="rounded" />
                    </div>
                    <div className="flex items-baseline gap-1 mb-1 h-9">
                        <Skeleton width="35%" height="30px" className="rounded" />
                        <Skeleton width="15%" height="12px" className="rounded" />
                    </div>
                    <Skeleton width="100%" height="16px" className="rounded" />
                </div>

                {/* 按钮骨架 */}
                <div className="relative mb-4">
                    <Skeleton width="100%" height="40px" className="rounded-full" />
                </div>

                {/* 下拉选择器骨架 */}
                {plan.tierSelector ? (
                    <div className="mb-4">
                        <Skeleton width="100%" height="42px" className="rounded-xl" />
                    </div>
                ) : null}

                {/* 功能列表骨架 */}
                <ul className="flex-1 space-y-2.5 border-t border-gray-100 pt-4">
                    {Array.from({ length: featuresCount }).map((_, i) => (
                        <li key={i} className="flex items-start gap-2.5 py-0.5">
                            <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                                <Skeleton width="13px" height="13px" className="rounded-full" />
                            </div>
                            <div className="flex-1 pt-0.5">
                                <Skeleton width={i % 2 === 0 ? "70%" : "85%"} height="14px" className="rounded" />
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    const actionClassName = clsx(
        'w-full py-2.5 rounded-full font-bold transition-all duration-200 text-sm tracking-wide flex items-center justify-center gap-2',
        isDisabledState
            ? 'bg-[#A3A0AE] text-white border-2 border-[#A3A0AE] shadow-none cursor-not-allowed'
            : 'bg-text-main text-white border-2 border-text-main shadow-[0_4px_12px_rgba(26,22,47,0.15)] hover:bg-black hover:border-black hover:shadow-lg hover:-translate-y-0.5'
    );
    const actionContent = isLoading ? (
        <>
            <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
            {t('website.pricing.processing')}
        </>
    ) : plan.buttonText;

    const handleOnboardingCardAction = () => {
        if (!isOnboarding || isDisabledState) return;
        onAction();
    };

    const handleOnboardingCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!isOnboarding || isDisabledState) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        onAction();
    };

    return (
        <div
            role={isOnboarding ? 'button' : undefined}
            tabIndex={isOnboarding ? (isDisabledState ? -1 : 0) : undefined}
            aria-label={isOnboarding ? plan.buttonText : undefined}
            aria-disabled={isOnboarding ? isDisabledState : undefined}
            onClick={isOnboarding ? handleOnboardingCardAction : undefined}
            onKeyDown={isOnboarding ? handleOnboardingCardKeyDown : undefined}
            className={clsx(
                'relative rounded-[24px] p-5 flex flex-col border transition-all duration-200 bg-white',
                isOnboarding && !isDisabledState && 'cursor-pointer focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 focus-visible:ring-offset-2',
                isOnboarding && isDisabledState && 'cursor-not-allowed',
                isCurrentTrialTone
                    ? 'border border-gray-300 shadow-md z-[1]'
                    : isScheduledTrialTone
                        ? 'border-2 border-gray-300 shadow-md z-[1]'
                        : isPopular
                            ? 'border-2 border-text-main shadow-[0_12px_28px_rgba(26,22,47,0.08)] z-10'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
            )}
        >
            {plan.badgeText ? (
                <div
                    className={clsx(
                        'absolute top-0 -translate-y-1/2 text-[10px] font-bold px-3 py-1 rounded-full tracking-wider whitespace-nowrap',
                        plan.cardTone === 'current-trial'
                            ? 'bg-white text-text-main border border-gray-300 shadow-sm'
                            : plan.cardTone === 'scheduled-trial'
                                ? 'bg-gray-100 text-text-main border border-gray-200 shadow-sm'
                                : 'bg-text-main text-white shadow-md',
                        plan.badgeAlign === 'right'
                            ? 'right-6 left-auto translate-x-0'
                            : 'left-1/2 -translate-x-1/2',
                    )}
                >
                    {plan.badgeText}
                </div>
            ) : isPopular ? (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#E6E3DE] bg-white px-2.5 py-0.5 text-[9px] font-semibold tracking-wide text-[#706C66] shadow-none">
                    {t('website.pricing.mostPopular')}
                </div>
            ) : null}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-bold text-text-main">
                        {plan.name}
                    </h3>
                    {plan.headerAction ? (
                        <div className="z-20 shrink-0">
                            {plan.headerAction}
                        </div>
                    ) : null}
                </div>

                <div className={clsx(
                    'mb-1',
                    hasNumericComparePrice
                        ? 'flex items-start'
                        : 'flex items-baseline gap-1 h-10'
                )}>
                    {plan.isCustom ? (
                        <span className="text-2xl font-bold text-text-main">
                            {t('website.pricing.custom')}
                        </span>
                    ) : (
                        <div className="flex flex-col items-start">
                            {hasNumericPrice ? (
                                hasNumericComparePrice ? (
                                    <div className="flex items-center gap-3">
                                        <span className="relative text-xl font-medium text-[#A3A0AE] tracking-tight inline-flex items-center">
                                            $
                                            <RollingPrice price={numericComparePrice} />
                                            <span className="pointer-events-none absolute left-[-1px] right-[-1px] top-[48%] h-[1.5px] -translate-y-1/2 rotate-[19deg] rounded-full bg-[#8C90A1]" />
                                        </span>
                                        <span className="text-4xl font-bold text-text-main tracking-tight inline-flex items-center leading-none">
                                            $
                                            <RollingPrice price={numericPrice} />
                                        </span>
                                        <div className="flex flex-col items-start justify-center gap-1">
                                            <span className="text-[#706C66] font-medium text-sm leading-none">
                                                {t('website.pricing.perMonth')}
                                            </span>
                                            {plan.priceNote ? (
                                                <p className="text-[11px] text-gray-500 leading-none whitespace-nowrap">
                                                    {plan.priceNote}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                ) : usesLeadingPeriodPrice ? (
                                    <div className="flex items-baseline gap-2 text-text-main">
                                        {plan.pricePrefix ? (
                                            <span className="whitespace-nowrap text-base font-semibold leading-none">
                                                {plan.pricePrefix}
                                            </span>
                                        ) : null}
                                        <span className="inline-flex items-center text-4xl font-bold leading-none tracking-tight">
                                            $
                                            <RollingPrice price={numericPrice} />
                                        </span>
                                        {plan.priceSuffix ? (
                                            <span className="whitespace-nowrap text-base font-semibold leading-none">
                                                {plan.priceSuffix}
                                            </span>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className={clsx(
                                        'flex',
                                        isCurrentTrialTone && plan.priceNote
                                            ? 'items-center gap-3'
                                            : 'items-baseline gap-1'
                                    )}>
                                        <div className="flex items-baseline gap-1">
                                            {plan.pricePrefix ? (
                                                <span className="text-sm font-medium leading-none text-[#706C66]">
                                                    {plan.pricePrefix}
                                                </span>
                                            ) : null}
                                            <span className="text-4xl font-bold text-text-main tracking-tight inline-flex items-center leading-none">
                                                $
                                                <RollingPrice price={numericPrice} />
                                            </span>
                                            {!isCurrentTrialTone || !plan.priceNote ? (
                                                <span className="text-[#706C66] font-medium text-sm">
                                                    {t('website.pricing.perMonth')}
                                                    {plan.priceSuffix}
                                                </span>
                                            ) : null}
                                        </div>
                                        {isCurrentTrialTone && plan.priceNote ? (
                                            <div className="flex flex-col items-start justify-center gap-1">
                                                <span className="text-[#706C66] font-medium text-sm leading-none">
                                                    {t('website.pricing.perMonth')}
                                                </span>
                                                <p className="text-[11px] text-gray-500 leading-none whitespace-nowrap">
                                                    {plan.priceNote}
                                                </p>
                                            </div>
                                        ) : null}
                                    </div>
                                )
                            ) : (
                                <span className="text-2xl font-bold text-text-main">
                                    {String(plan.price)}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <p className="text-xs font-medium text-[#706C66] min-h-[36px] leading-relaxed line-clamp-2">
                    {plan.description}
                </p>
                {!plan.isCustom && plan.priceNote && !hasNumericComparePrice && !isCurrentTrialTone ? (
                    <p className="text-[11px] text-gray-500 mt-1">
                        {plan.priceNote}
                    </p>
                ) : null}
            </div>

            <div className="relative mb-4 group/plan-button">
                {plan.bubbleText ? (
                    <div role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-3 w-max max-w-[360px] -translate-x-1/2 rounded-lg bg-[#1A162F] px-5 py-3 text-center text-[13px] font-semibold leading-[1.2] text-white opacity-0 shadow-lg transition-all duration-150 group-hover/plan-button:translate-y-0 group-hover/plan-button:opacity-100 group-focus-within/plan-button:opacity-100">
                        {plan.bubbleText}
                    </div>
                ) : null}

                {isOnboarding ? (
                    <div aria-hidden="true" className={actionClassName}>
                        {actionContent}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={onAction}
                        disabled={isDisabledState}
                        className={actionClassName}
                    >
                        {actionContent}
                    </button>
                )}
            </div>

            {plan.tierSelector ? (
                <div className="mb-4">
                    {plan.tierSelector}
                </div>
            ) : null}

            {shouldShowFeatureContent ? (
                <div
                  className={clsx(
                      'flex-1 border-t border-gray-100 pt-4',
                      hasFeatureGroups && 'space-y-8'
                  )}
                >
                  {plan.featureMode === 'grouped' ? (
                    <>
                        {/* Group 1: Workspace */}
                        <div>
                            <h4 className="text-sm font-semibold text-text-main mb-2.5">
                                {plan.workspaceGroupLabel || t('website.pricing.groupWorkspaceWorkUnits')}
                            </h4>
                            <ul className="space-y-2">
                                {plan.featureGroups.workspace.map((feature) => {
                                    const isHighlighted = plan.highlightLimits;
                                    return (
                                        <li key={feature.id} className="flex items-start gap-2.5 py-0.5">
                                            <span
                                                className={clsx(
                                                    'flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none',
                                                    isHighlighted ? 'text-[#2F7BFF]' : 'text-[#4F4C47]'
                                                )}
                                            >
                                                ✓
                                            </span>
                                            <span
                                                className={clsx(
                                                    'text-sm leading-relaxed',
                                                    isHighlighted
                                                        ? 'text-[#2F7BFF] font-semibold'
                                                        : 'text-[#302E2A] font-normal'
                                                )}
                                            >
                                                {isHighlighted ? feature.label : renderFormattedText(feature.label)}
                                            </span>
                                        </li>
                                    );
                                })}
                                {isOnboarding && plan.trialBenefit ? (
                                    <li className="flex items-start gap-2.5 py-0.5">
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
                                            ✓
                                        </span>
                                        <span className="text-sm leading-relaxed text-[#302E2A] font-normal">
                                            {plan.trialBenefit.value}
                                        </span>
                                    </li>
                                ) : null}
                                {/* 1. Monthly Work Units */}
                                {shouldShowWorkUnitBenefits && (() => {
                                    const monthlyWorkUnits = plan.featureGroups.workUnits.find(
                                        (f) => f.id === 'monthly-work-units'
                                    );
                                    if (!monthlyWorkUnits) return null;
                                    return (
                                        <li key={monthlyWorkUnits.id} className="flex items-start gap-2.5 py-0.5">
                                            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
                                                ✓
                                            </span>
                                            <span className="text-sm leading-relaxed text-[#302E2A] font-normal">
                                                {renderFormattedText(monthlyWorkUnits.label)}
                                            </span>
                                        </li>
                                    );
                                })()}

                                {/* 2. Estimated Capacity */}
                                {shouldShowWorkUnitBenefits && plan.estimatedCapacity ? (
                                    <EstimatedCapacityFeature
                                        label={plan.estimatedCapacity}
                                        tooltip={t('website.pricing.estimatedTaskCountTooltip')}
                                    />
                                ) : null}

                                {/* 3. Work Unit Top-ups */}
                                {shouldShowWorkUnitBenefits && (() => {
                                    const workUnitTopUps = plan.featureGroups.workUnits.find(
                                        (f) => f.id === 'work-unit-top-ups'
                                    );
                                    if (!workUnitTopUps) return null;
                                    return (
                                        <li key={workUnitTopUps.id} className="flex items-start gap-2.5 py-0.5">
                                            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
                                                ✓
                                            </span>
                                            <span className="text-sm leading-relaxed text-[#302E2A] font-normal">
                                                {renderFormattedText(workUnitTopUps.label)}
                                            </span>
                                        </li>
                                    );
                                })()}
                            </ul>
                        </div>

                        {/* Group 2: Built-in AI capabilities */}
                        {shouldShowBuiltInCapabilities ? (
                            <div>
                                <h4 className="text-sm font-semibold text-text-main mb-2.5">
                                    {plan.builtInCapabilitiesLabel}
                                </h4>
                                <div className="space-y-2">
                                    {plan.builtInModels?.map((model) => (
                                        <div key={model.name} className="flex items-center gap-2">
                                            <img
                                                src={model.iconSrc}
                                                alt=""
                                                aria-hidden="true"
                                                className="h-4 w-4 shrink-0 object-contain"
                                            />
                                            <span className="text-xs font-normal leading-4 text-[#4F4C47]">
                                                {model.capabilityLabel || model.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {/* Group 3: Training & Integrations */}
                        {shouldShowPaidBenefits && (
                            <div>
                                <h4 className="text-sm font-semibold text-text-main mb-2.5">
                                    {t('website.pricing.groupTrainingIntegrations')}
                                </h4>
                                <ul className="space-y-2">
                                    {plan.featureGroups.trainingAndIntegrations.map((feature) => (
                                        <li key={feature.id} className="flex items-start gap-2.5 py-0.5">
                                            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
                                                ✓
                                            </span>
                                            <span className="text-sm leading-relaxed text-[#302E2A] font-normal">
                                                {renderFormattedText(feature.label)}
                                                {feature.badge === 'team-exclusive' ? (
                                                    <span className="inline-flex items-center rounded bg-text-main px-1.5 py-0.5 text-[10px] font-semibold text-white ml-1.5 shrink-0 align-middle">
                                                        {t('website.pricing.teamExclusiveBadge')}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </li>
                                    ))}
                                    {plan.showIntegrations ? (
                                        <IncludedIntegrations
                                            label={t('website.pricing.includedIntegrations')}
                                            planName={plan.name}
                                        />
                                    ) : null}
                                </ul>
                            </div>
                        )}
                    </>
                ) : (
                    <ul className="space-y-2.5">
                        {/* Fallback for mock plans with fewer features (renders estimatedCapacity first) */}
                        {plan.estimatedCapacity ? (
                            <EstimatedCapacityFeature
                                label={plan.estimatedCapacity}
                                tooltip={t('website.pricing.estimatedTaskCountTooltip')}
                            />
                        ) : null}
                        {(plan.features || []).map((feature, i) => {
                            const isHeader = feature === 'Everything in Solo, plus';
                            return (
                                <li
                                    key={`feature-fallback-${i}`}
                                    className={clsx(
                                        'flex items-start gap-2.5 py-0.5',
                                        isHeader && 'pt-1'
                                    )}
                                >
                                    {!isHeader && (
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
                                            ✓
                                        </span>
                                    )}
                                    <span
                                        className={clsx(
                                            'text-sm leading-relaxed',
                                            isHeader ? 'text-text-main font-semibold' : 'text-[#302E2A] font-normal',
                                        )}
                                    >
                                        {isHeader ? feature : renderFormattedText(feature)}
                                    </span>
                                </li>
                            );
                        })}

                        {/* Fallback built-in AI capabilities with perfectly centered header checkmark */}
                        {plan.builtInCapabilitiesLabel && plan.builtInModels?.length ? (
                            <li className="py-0.5">
                                <div className="flex items-center gap-2.5">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
                                        ✓
                                    </span>
                                    <span className="text-sm font-normal leading-relaxed text-[#302E2A]">
                                        {plan.builtInCapabilitiesLabel}
                                    </span>
                                </div>
                                <div className="pl-7 mt-2 space-y-2">
                                    {plan.builtInModels.map((model) => (
                                        <div key={model.name} className="flex items-center gap-2">
                                            <img
                                                src={model.iconSrc}
                                                alt=""
                                                aria-hidden="true"
                                                className="h-4 w-4 shrink-0 object-contain"
                                            />
                                            <span className="text-xs font-normal leading-4 text-[#4F4C47]">
                                                {model.capabilityLabel || model.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </li>
                        ) : null}

                        {/* Fallback included integrations */}
                        {plan.showIntegrations ? (
                            <IncludedIntegrations
                                label={t('website.pricing.includedIntegrations')}
                                planName={plan.name}
                            />
                        ) : null}
                    </ul>
                )}
                </div>
            ) : null}

            {plan.footerText && plan.footerActionLabel && onFooterAction ? (
                <div className="mt-4 border-t border-gray-100 pt-4">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onFooterAction();
                        }}
                        className="text-left text-xs font-medium text-[#4F4C47] transition-colors hover:text-text-main"
                    >
                        {plan.footerText}
                        {' '}
                        <span className="font-semibold text-text-main">{plan.footerActionLabel}</span>
                    </button>
                </div>
            ) : null}

        </div>
    );
};

export default PricingCard;
